import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ToggleLeft, ToggleRight } from 'lucide-react';
type EditorMode = 'tree' | 'text';
interface ObjectDataEditorProps {
  data: Record<string, unknown> | null;
  onChange: (data: Record<string, unknown>) => void;
  mode?: EditorMode;
  className?: string;
}
export function ObjectDataEditor({
  data,
  onChange,
  mode: initialMode = 'tree',
  className
}: ObjectDataEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);
  const isInternalChangeRef = useRef(false);
  const [mode, setMode] = useState<EditorMode>(initialMode);
  useEffect(() => {
    let destroyed = false;
    async function initEditor() {
      if (!containerRef.current) return;
      const {
        createJSONEditor
      } = await import('vanilla-jsoneditor');
      if (destroyed) return;

      // Destroy previous editor if it exists
      if (editorRef.current) {
        editorRef.current.destroy();
      }
      editorRef.current = createJSONEditor({
        target: containerRef.current,
        props: {
          content: {
            json: data ?? {}
          },
          mode: mode as any,
          onChange: (updatedContent: any) => {
            try {
              isInternalChangeRef.current = true;
              if (updatedContent.json !== undefined) {
                onChange(updatedContent.json as Record<string, unknown>);
              } else if (updatedContent.text !== undefined) {
                const parsed = JSON.parse(updatedContent.text);
                onChange(parsed);
              }
            } catch {
              // Invalid JSON during editing in text mode - ignore until valid
            }
          }
        }
      });
    }
    initEditor();
    return () => {
      destroyed = true;
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [mode]); // Re-create editor when mode changes

  // Update content when data prop changes externally (not from editor itself)
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    if (editorRef.current && editorRef.current.updateProps) {
      editorRef.current.updateProps({
        content: {
          json: data ?? {}
        }
      });
    }
  }, [data]);
  const toggleMode = () => {
    setMode(prev => prev === 'tree' ? 'text' : 'tree');
  };
  return <div className={className}>
      <div className="flex items-center justify-end mb-2">
        <Button type="button" variant="outline" size="sm" onClick={toggleMode} className="gap-2 cursor-pointer">
          {mode === 'tree' ? <>
              <ToggleLeft className="h-4 w-4" />
              Tree Mode
            </> : <>
              <ToggleRight className="h-4 w-4" />
              Text Mode
            </>}
        </Button>
      </div>
      <div ref={containerRef} className="border border-gray-200 rounded-lg overflow-hidden min-h-[400px]" />
    </div>;
}